// Shared lightweight org directory cache (id + name) used by both the OrgPicker
// (instant client-side typeahead) and the landing directory grid. Loading the
// `organizations` collection is the single most expensive thing on this page,
// so we memoize the fetch per-env at module scope: the picker and the landing
// resolve from the same in-flight promise instead of each hitting Firestore.

import { useEffect, useState } from "react";
import { collection, getDocs, limit, query } from "firebase/firestore";
import { firestoreDev, firestoreProd } from "../../firebase";

const ORG_CACHE_LIMIT = 1000;

const orgLabel = (org) =>
  org?.name ||
  org?.organizationName ||
  org?.companyName ||
  org?.orgName ||
  org?.id ||
  "Unknown organization";

export const normalizeOrg = (id, data = {}) => ({
  id,
  name: orgLabel({ id, ...data }),
});

// env -> Promise<Org[]>
const memo = new Map();

function loadOrgCache(env) {
  if (memo.has(env)) return memo.get(env);
  const firestore = env === "dev" ? firestoreDev : firestoreProd;
  if (!firestore) return Promise.resolve([]);
  const promise = getDocs(
    query(collection(firestore, "organizations"), limit(ORG_CACHE_LIMIT)),
  )
    .then((snap) =>
      snap.docs
        .map((d) => normalizeOrg(d.id, d.data()))
        .sort((a, b) => a.name.localeCompare(b.name)),
    )
    .catch((err) => {
      // Drop the cached rejection so a later mount can retry.
      memo.delete(env);
      console.error("[useOrgCache] org cache load failed", err);
      return [];
    });
  memo.set(env, promise);
  return promise;
}

export function useOrgCache(env) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadOrgCache(env).then((list) => {
      if (cancelled) return;
      setOrgs(list);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [env]);

  return { orgs, loading };
}
